import axios from "axios";
import { API_BASE_URL, HUBBLE_LITERATURE_URL } from "../config";
import React, { createElement } from "react";

// Helper function to convert references in text to clickable links
export const parseReferences = (
  text: string,
  references: Record<string, any>,
): React.ReactNode => {
  if (!text || !references) return text;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Match patterns like [1], [ref1], [key], etc.
  const regex = /\[([^\]]+)\]/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const key = match[1];
    const ref = references[key];

    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the reference link if we have a pid
    if (ref && ref.pid) {
      parts.push(
        createElement(
          "a",
          {
            key: `ref-${match.index}`,
            href: `${HUBBLE_LITERATURE_URL}${ref.pid}`,
            target: "_blank",
            rel: "noopener noreferrer",
            title: ref.title || `Reference ${key}`,
            className:
              "inline-flex items-center gap-0.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium hover:underline decoration-blue-400 dark:decoration-blue-500 hover:decoration-blue-600 dark:hover:decoration-blue-400 decoration-1 underline-offset-2 transition-colors duration-150",
          },
          `[${key}]`,
        ),
      );
    } else {
      // If no reference found, just add the bracketed text as-is
      parts.push(`[${key}]`);
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

// Helper function to parse markdown links and convert them to clickable links
export const parseMarkdownLinks = (text: string) => {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the link
    parts.push(
      createElement(
        "a",
        {
          key: match.index,
          href: match[2],
          target: "_blank",
          rel: "noopener noreferrer",
          className:
            "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline",
        },
        match[1],
      ),
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 1 ? parts : text;
};

export const calculateTIssueExpression = (data: any) => {
  const ytemp = data.map((itm: any) => itm.nTPM);
  const ltemp = data.map((itm: any) => itm.target);
  const traces = {
    // x: [1],
    y: ytemp,
    mode: "markers+text",
    type: "scatter",
    name: "npTm",
    text: ltemp,
    textposition: "top center",
    textfont: {
      family: "Raleway, sans-serif",
    },
    marker: { size: 10 },
  };
  return [traces];
};

export const firstUpperCase = (string: string) => {
  return (
    String(string[0]).toUpperCase() + String(string).slice(1).toLowerCase()
  );
};

export const diseaseassociationsData = (data: any) => {
  const tmp = [];
  for (const key in data) {
    if (data[key]) tmp.push(1);
    else tmp.push(0);
  }
  return [tmp];
};

export const scrnaseqapi = async (
  obj: any,
  therapeuticAreaInternal: string,
  gene_id: string,
  setScrnaseqdata: any,
) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/tess/gene-explorer/gene-explorer/scrnaseq-data?gene_id=${gene_id}&dataset_name=${
      obj.name
    }&indication=${firstUpperCase(therapeuticAreaInternal)}`,
  );
  let xvalues: any = [];
  const zvalues: any = [];
  const tmpArr = response.data.data[obj.name];
  tmpArr.forEach(function (itmobj: any) {
    const itmobjvalues = Object.values(itmobj).map((itm: any) => {
      return isNaN(parseFloat(itm)) ? 0 : parseFloat(itm);
    });
    xvalues = Object.keys(itmobj);
    zvalues.push(Object.values(itmobjvalues));
  });
  const flatArray = zvalues.flat(Infinity);
  setScrnaseqdata({
    name: obj.name,
    xvalues: xvalues,
    yvalues: [
      Math.floor(Math.min(...flatArray)),
      Math.floor(Math.min(...flatArray)) === Math.ceil(Math.max(...flatArray))
        ? Math.ceil(Math.max(...flatArray)) + 1
        : Math.ceil(Math.max(...flatArray)),
    ],
    zvalues: zvalues,
    description: obj.description,
    rowNames: response.data.data[`${obj.name}_row_names`],
  });
};
